import { supabase } from '../lib/supabase';

/**
 * Profile Service
 * Handles all profile-related operations with Supabase
 */

/**
 * Get user profile by ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} User profile data
 */
export const getUserProfile = async (userId) => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

/**
 * Update user profile
 * @param {string} userId - User ID
 * @param {Object} updates - Profile updates
 * @returns {Promise<Object>} Updated profile data
 */
export const updateUserProfile = async (userId, updates) => {
  try {
    // Convert camelCase to snake_case for database
    const dbUpdates = {
      full_name: updates?.fullName,
      email: updates?.email,
      phone: updates?.phone,
      location: updates?.location,
      profile_photo: updates?.profilePhoto,
      experience_level: updates?.experienceLevel,
      expected_salary: updates?.expectedSalary,
      preferred_locations: updates?.preferredLocations,
      remote_preference: updates?.remotePreference,
      skills: updates?.skills,
      resume_files: updates?.resumeFiles,
      bio: updates?.bio,
      linkedin: updates?.linkedin,
      portfolio: updates?.portfolio,
      current_ctc: updates?.currentCtc,
      notice_period: updates?.noticePeriod,
      willing_to_relocate: updates?.willingToRelocate,
      open_to_remote: updates?.openToRemote,
      company_name: updates?.companyName
    };

    // Remove undefined values
    Object.keys(dbUpdates)?.forEach(key => {
      if (dbUpdates?.[key] === undefined) {
        delete dbUpdates?.[key];
      }
    });

    const { data, error } = await supabase?.from('user_profiles')?.update(dbUpdates)?.eq('id', userId)?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

/**
 * Convert database profile to camelCase for React
 * @param {Object} dbProfile - Database profile object
 * @returns {Object} CamelCase profile object
 */
export const convertDbProfileToCamelCase = (dbProfile) => {
  if (!dbProfile) return null;

  return {
    id: dbProfile?.id,
    fullName: dbProfile?.full_name,
    email: dbProfile?.email,
    phone: dbProfile?.phone,
    location: dbProfile?.location,
    profilePhoto: dbProfile?.profile_photo,
    experienceLevel: dbProfile?.experience_level,
    expectedSalary: dbProfile?.expected_salary,
    preferredLocations: dbProfile?.preferred_locations || [],
    remotePreference: dbProfile?.remote_preference,
    skills: dbProfile?.skills || [],
    resumeFiles: dbProfile?.resume_files || [],
    bio: dbProfile?.bio,
    linkedin: dbProfile?.linkedin,
    portfolio: dbProfile?.portfolio,
    currentCtc: dbProfile?.current_ctc,
    noticePeriod: dbProfile?.notice_period,
    willingToRelocate: dbProfile?.willing_to_relocate,
    openToRemote: dbProfile?.open_to_remote,
    companyName: dbProfile?.company_name,
    role: dbProfile?.role,
    createdAt: dbProfile?.created_at,
    updatedAt: dbProfile?.updated_at
  };
};

/**
 * Get profile with application data for candidate management
 * @param {string} applicationId - Application ID
 * @returns {Promise<Object>} Combined profile and application data
 */
export const getProfileWithApplicationData = async (applicationId) => {
  try {
    const { data, error } = await supabase?.from('job_applications')?.select(`
        *,
        applicant:user_profiles!job_applications_applicant_id_fkey (
          id,
          full_name,
          email,
          phone,
          location,
          profile_photo,
          experience_level,
          expected_salary,
          preferred_locations,
          remote_preference,
          skills,
          linkedin,
          portfolio,
          bio
        )
      `)?.eq('id', applicationId)?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching profile with application:', error);
    throw error;
  }
};